import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useCart } from '../../../contexts/CartContext';
import { toast } from 'react-hot-toast';

const ProductCard = ({ product, onProductClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const { addToCart, cartItems } = useCart();
  
  // Vérifier si le produit est déjà dans le panier
  useEffect(() => {
    const itemInCart = cartItems.find(item => item.id === product.id);
    setIsInCart(!!itemInCart);
  }, [cartItems, product.id]);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    // Empêcher les clics multiples
    if (isAddingToCart) return;
    
    // Désactiver le bouton immédiatement
    setIsAddingToCart(true);
    
    // Créer une promesse avec un délai pour assurer l'affichage de l'état de chargement
    await new Promise(resolve => {
      // Créer le payload du panier
      const isSale = product.sale && product.sale.isActive;
      const cartPayload = {
        id: product.id,
        name: product.name,
        price: isSale ? parseFloat(product.sale.discountedPrice) : parseFloat(product.price),
        originalPrice: isSale ? parseFloat(product.price) : undefined,
        image: product.urlImage,
        size: product.size || 'Unique',
        color: product.color || 'Standard',
        source: 'From Collections',
        stock: product.stock || 0
      };

      // Ajouter au panier
      addToCart(cartPayload);
      
      // Afficher la notification de succès de manière plus visible
      toast((t) => (
        <div className="flex items-center space-x-3">
          <div className="bg-white p-1 rounded-full">
            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <div>
            <p className="font-semibold text-white">Produit ajouté !</p>
            <p className="text-sm text-white/90">Votre article a été ajouté au panier</p>
          </div>
        </div>
      ), {
        position: 'bottom-center',
        duration: 3000,
        style: {
          background: '#10B981',
          color: '#fff',
          padding: '16px 24px',
          borderRadius: '12px',
          maxWidth: '90%',
          width: 'auto',
          margin: '0 auto 20px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        },
        className: 'animate-bounce',
        iconTheme: {
          primary: '#fff',
          secondary: '#10B981'
        }
      });
      
      // Maintenir l'état de chargement pendant 1.5 secondes pour un meilleur retour visuel
      setTimeout(() => {
        resolve();
      }, 1500);
    }).catch(error => {
      console.error('Erreur lors de l\'ajout au panier:', error);
      toast.error("❌ Erreur lors de l'ajout au panier", {
        position: 'top-center',
        duration: 2000,
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '12px 24px',
          borderRadius: '8px',
          fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }
      });
    }).finally(() => {
      // Réactiver le bouton après 1.5 secondes
      setTimeout(() => {
        setIsAddingToCart(false);
      }, 500);
    });
  };

  const isSale = product.sale && product.sale.isActive;
  const displayPrice = isSale ? parseFloat(product.sale.discountedPrice) : parseFloat(product.price);
  const originalPrice = isSale ? parseFloat(product.price) : null;

  return (
    <motion.div
      className="bg-surface rounded-lg overflow-hidden border border-street cursor-pointer group flex flex-col h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onProductClick(product)}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <div className="relative aspect-square overflow-hidden">
        <Image
          src={isHovered && product.urlImageHover ? product.urlImageHover : product.urlImage}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />

        {/* Overlay actions */}
        <motion.div
          className="absolute inset-0 bg-black/40 flex items-center justify-center space-x-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <Button 
            variant="secondary" 
            size="icon" 
            onClick={(e) => { 
              e.stopPropagation(); 
              // Gérer les favoris ici si nécessaire
            }}
          >
            <Icon name="Heart" size={20} />
          </Button>
        </motion.div>

        {product.sale && product.sale.isActive && (
          <span className="absolute top-2 right-2 bg-accent text-accent-foreground px-2 py-1 rounded text-xs font-bold">
            -{product.sale.discountPercentage}%
          </span>
        )}

        {/* Stock badge */}
        {product.stock < 5 && (
          <span className="absolute top-2 left-2 bg-warning text-warning-foreground px-2 py-1 rounded text-xs font-bold">
            Only {product.stock} left
          </span>
        )}
      </div>

      <div className="p-4 flex-grow flex flex-col">
        <h3 className="w-full block font-heading font-bold text-foreground group-hover:text-accent truncate">{product.name}</h3>
        {product.description && <p className="text-muted-foreground text-sm truncate mt-1">{product.description}</p>}
        
        <div className="mt-auto pt-4">
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-baseline space-x-2">
              <span className="font-heading font-bold text-lg text-accent">
                {Number(displayPrice).toFixed(3)} TND
              </span>
              {originalPrice && (
                <span className="text-muted-foreground line-through text-sm">
                  {Number(originalPrice).toFixed(3)} TND
                </span>
              )}
            </div>
          </div>
          
          <Button 
            variant={isInCart ? 'ghost' : 'outline'}
            className={`w-full transition-all ${
              isInCart 
                ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-default' 
                : `border-accent text-accent hover:bg-accent hover:text-white ${isAddingToCart ? 'opacity-75 cursor-not-allowed' : ''}`
            }`}
            onClick={handleAddToCart}
            disabled={isAddingToCart || isInCart || (product.stock <= 0)}
          >
            {isAddingToCart ? (
              <>
                <Icon name="Loader" className="animate-spin mr-2" size={16} />
                Ajout en cours...
              </>
            ) : isInCart ? (
              <>
                
                Déjà au panier
              </>
            ) : (
              <>
                <Icon name="ShoppingBag" className="mr-2" size={16} />
                {product.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
